<template>
    <el-icon @click="open()" style="margin: 0px 5px 0px 10px">
        <Service />
    </el-icon>
    <el-drawer v-model="drawer" body-class="drawer-body" :with-header="false" size="60%">
        <!-- 头部 -->
        <div class="drawer-header">
            <span class="drawer-title">AI客服</span>
            <div class="header-actions">
                <el-button text @click="openHistoryDialog" :icon="Document">历史会话</el-button>
                <el-button text @click="openAttachmentDialog" :icon="Paperclip">查看附件</el-button>
                <el-button text @click="createNewSessionFun" :icon="DocumentAdd">新建会话</el-button>
            </div>
        </div>
        <!-- 聊天内容 -->
        <div class="ai-chat">
            <!-- 对话历史 -->
            <div class="chat-history" ref="chatHistoryRef">
                <template v-for="(msg, index) in messageList" :key="index">
                    <ChatMessage :message="msg" :user-avatar="userStore.avatar || defaultAvatar"
                        :ai-avatar="defaultAvatar" />
                </template>
                <!-- AI思考中 -->
                <div v-if="isAiThinking" class="thinking-indicator">
                    <div class="ai-side">
                        <el-avatar :src="defaultAvatar" style="flex-shrink: 0;" />
                        <div class="content" style="margin-left: 10px;">
                            <div class="thinking-dots">
                                <span class="dot"></span>
                                <span class="dot"></span>
                                <span class="dot"></span>
                            </div>
                        </div>
                    </div>
                    <div class="ai-side empty-side"></div>
                </div>
                <!-- 报告生成中提示 -->
                <div v-if="reportGenerating" class="report-generating-indicator">
                    <el-icon class="report-icon"><Document /></el-icon>
                    <span>正在生成报告,完成后会自动推送到会话里...</span>
                </div>
                <!-- 空状态 -->
                <div v-if="messageList.length === 0 && !isAiThinking" class="empty-state">
                    <el-icon :size="48">
                        <ChatDotRound />
                    </el-icon>
                    <p>暂无消息记录，开始对话吧</p>
                </div>
            </div>
            <!-- 输入区域 -->
            <div class="chat-input">
                <textarea v-model="userInput" placeholder="输入您的问题，按Enter发送..."
                    @keydown.enter.exact.prevent="sendMessage" :disabled="loading" rows="2"></textarea>
                <button @click="sendMessage" :disabled="loading || !userInput.trim()">
                    {{ loading ? '思考中...' : '发送' }}
                </button>
            </div>
        </div>

        <!-- 历史会话弹窗 -->
        <HistorySessionDialog v-model="historyDialogVisible" :current-session-id="currentSessionId"
            :user-id="userStore.userId" @select="handleSessionSelect" @delete="handleSessionDelete" />

        <!-- 附件列表弹窗 -->
        <AttachmentListDialog v-model="attachmentDialogVisible" :session-id="currentSessionId" />
    </el-drawer>
</template>

<script setup>
import { aiChatStream, listChat, getOrCreateSession, createNewSessionApi } from '@/api/ai/chat';
import { ref, nextTick, watch } from 'vue';
import { ElMessage } from 'element-plus';
import { ChatDotRound, Document, DocumentAdd, Paperclip } from '@element-plus/icons-vue';
import useUserStore from '@/store/modules/user';
import ChatMessage from './ChatMessage.vue';
import HistorySessionDialog from './HistorySessionDialog.vue';
import AttachmentListDialog from './AttachmentListDialog.vue';

const userStore = useUserStore();
const defaultAvatar = 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png';

// 状态
const drawer = ref(false);
const messageList = ref([]);
const userInput = ref('');
const loading = ref(false);
const isAiThinking = ref(false); // AI思考中状态
const reportGenerating = ref(false); // 报告生成中状态(generateReport Tool 调用期间)
const reportPollTimer = ref(null); // 报告生成期间轮询 chat history 找 file 消息
const chatHistoryRef = ref(null);

// 会话相关状态
const currentSessionId = ref('');
const historyDialogVisible = ref(false);
const attachmentDialogVisible = ref(false);

// 打开对话框
async function open() {
    drawer.value = true;
    await initSession();
}

// 关闭抽屉时清理轮询
watch(drawer, (val) => {
    if (!val) {
        stopReportPoll();
        reportGenerating.value = false;
    }
});

// 初始化会话(获取或创建)
async function initSession() {
    try {
        const res = await getOrCreateSession(userStore.userId);
        if (res?.code === 200 && res.data) {
            currentSessionId.value = res.data.sessionId;
            await queryChatList();
        }
    } catch (error) {
        console.error('初始化会话失败:', error);
    }
}

// 加载聊天历史
async function queryChatList() {
    if (!currentSessionId.value) return;
    try {
        const res = await listChat(currentSessionId.value);
        if (res?.code === 200 && res.data) {
            messageList.value = res.data.map(msg => ({
                ...msg,
                displayContent: msg.content,
                loading: false
            }));
            // 历史里有 file 类型消息,说明报告早就生成完了,确保提示条不残留
            const hasFile = res.data.some(m => m.messageType === 'file');
            if (hasFile) reportGenerating.value = false;
            nextTick(scrollBottom);
        }
    } catch (error) {
        console.error('加载聊天历史失败:', error);
    }
}

// 解析 toolCalls(可能是 JSON 字符串或数组)
function parseToolCallList(toolCalls) {
    if (!toolCalls) return []
    if (Array.isArray(toolCalls)) return toolCalls
    if (typeof toolCalls === 'string') {
        try { return JSON.parse(toolCalls) } catch { return [] }
    }
    return []
}

// 启动报告轮询:每 3 秒拉一次 chat history,直到出现 messageType=file 消息后停止
function startReportPoll() {
    stopReportPoll()
    reportPollTimer.value = setInterval(async () => {
        if (!currentSessionId.value) return
        try {
            const res = await listChat(currentSessionId.value)
            if (res?.code !== 200 || !res.data) return
            // 检测新出现的 file 消息(按时间倒序判断)
            const newFileMsgs = res.data.filter(m =>
                m.messageType === 'file' &&
                !messageList.value.some(local =>
                    local.messageType === 'file' && local.content === m.content
                )
            )
            if (newFileMsgs.length > 0) {
                // 追加新消息
                newFileMsgs.forEach(m => {
                    messageList.value.push({
                        ...m,
                        displayContent: m.content,
                        loading: false
                    })
                })
                reportGenerating.value = false
                stopReportPoll()
                nextTick(scrollBottom)
            }
        } catch (e) {
            console.error('报告轮询失败:', e)
        }
    }, 3000)
}

function stopReportPoll() {
    if (reportPollTimer.value) {
        clearInterval(reportPollTimer.value)
        reportPollTimer.value = null
    }
}

// 发送消息 - 真流式处理
async function sendMessage() {
    const content = userInput.value.trim();
    if (!content || loading.value) return;

    // 确保有会话id
    if (!currentSessionId.value) {
        await initSession();
    }

    // 添加用户消息
    messageList.value.push({
        role: 'user',
        content,
        displayContent: content,
        loading: false
    });
    userInput.value = '';
    // DOM更新后再滚动
    await nextTick();
    scrollBottom();

    loading.value = true;
    isAiThinking.value = true;
    await nextTick();
    scrollBottom();

    try {
        // 参考代码：isFirstChunk 追踪是否正在流式传输
        let isFirstChunk = true;

        for await (const event of aiChatStream(currentSessionId.value, content)) {
            // 处理错误
            if (event.node === 'error') {
                // 错误时追加一条错误消息
                messageList.value.push({
                    role: 'assistant',
                    content: '',
                    displayContent: '',
                    messageType: 'assistant',
                    error: event.chunk || '服务异常',
                    loading: false
                });
                isAiThinking.value = false;
                await nextTick();
                scrollBottom();
                continue;
            }

            // 处理流式片段 (chunk)
            if (event.chunk) {
                isAiThinking.value = false; // 开始收到内容，停止思考动画

                if (isFirstChunk) {
                    // 参考代码：第一个chunk创建新消息
                    messageList.value.push({
                        role: 'assistant',
                        content: event.chunk,
                        displayContent: event.chunk,
                        messageType: 'assistant',
                        toolCalls: [],
                        toolResponses: [],
                        loading: true
                    });
                    isFirstChunk = false;
                } else {
                    // 参考代码：后续chunk追加到最后一个消息
                    const lastIndex = messageList.value.length - 1;
                    messageList.value[lastIndex].content += event.chunk;
                    messageList.value[lastIndex].displayContent += event.chunk;
                }
                await nextTick();
                scrollBottom();
            }

            // 处理完整消息
            if (event.message) {
                const messageType = event.message.messageType;

                // 处理 tool/tool-confirm：直接追加，重置 isFirstChunk
                if (messageType === 'tool') {
                    isAiThinking.value = false; // 工具开始执行，停止思考动画
                    messageList.value.push({
                        role: 'tool',
                        messageType: 'tool',
                        toolCalls: [],
                        toolResponses: event.message.toolResponses || [],
                        loading: false
                    });
                    // tool 响应里若有 generateReport 的返回(status=submitted),仍视为"正在生成",
                    // 等 messageType=file 的消息到达后才清掉
                    isFirstChunk = true; // 重置，下一个 assistant 是新消息
                    await nextTick();
                    scrollBottom();
                } else if (messageType === 'tool-confirm') {
                    isAiThinking.value = false;
                    messageList.value.push({
                        role: 'tool-confirm',
                        messageType: 'tool-confirm',
                        toolCalls: event.message.toolCalls || [],
                        toolResponses: [],
                        content: event.message.content || '',
                        loading: false
                    });
                    isFirstChunk = true;
                    await nextTick();
                    scrollBottom();
                } else if (messageType === 'tool-request') {
                    isAiThinking.value = false; // 工具请求开始，停止思考动画
                    messageList.value.push({
                        role: 'tool-request',
                        messageType: 'tool-request',
                        toolCalls: event.message.toolCalls || [],
                        toolResponses: [],
                        loading: false
                    });
                    // 检测是否调用了报告生成 Tool
                    const tcArr = parseToolCallList(event.message.toolCalls);
                    if (tcArr.some(t => (t.name || '').toLowerCase().includes('generatereport'))) {
                        reportGenerating.value = true;
                        startReportPoll();
                    }
                    isFirstChunk = true;
                    await nextTick();
                    scrollBottom();
                } else if (messageType === 'assistant') {
                    // 参考代码：assistant/tool-request 可能携带完整内容
                    if (isFirstChunk) {
                        // 新建消息
                        const newMsg = {
                            role: 'assistant',
                            content: event.message.content || '',
                            displayContent: event.message.content || '',
                            messageType: 'assistant',
                            toolCalls: event.message.toolCalls || [],
                            toolResponses: [],
                            loading: true
                        };
                        messageList.value.push(newMsg);
                        isFirstChunk = false;
                    } else {
                        // 更新最后一条消息
                        const lastIndex = messageList.value.length - 1;
                        const existingContent = messageList.value[lastIndex].content || '';
                        const newContent = event.message.content || '';

                        // 只有当 message.content 比现有内容更长时才更新（表示这是完整内容）
                        // 否则保留累加的内容
                        if (newContent.length > existingContent.length) {
                            messageList.value[lastIndex].content = newContent;
                            messageList.value[lastIndex].displayContent = newContent;
                        }
                        if (event.message.toolCalls?.length > 0) {
                            messageList.value[lastIndex].toolCalls = event.message.toolCalls;
                        }
                    }
                    await nextTick();
                    scrollBottom();
                }
            }
        }

        // 流结束
        isAiThinking.value = false;
        // 标记所有消息为非加载中
        messageList.value.forEach(msg => {
            msg.loading = false;
        });

    } catch (error) {
        console.error('AI响应异常:', error);
        messageList.value.push({
            role: 'assistant',
            content: '',
            displayContent: '',
            messageType: 'assistant',
            error: '抱歉，服务出现异常，请稍后重试。',
            loading: false
        });
        isAiThinking.value = false;
        await nextTick();
        scrollBottom();
    } finally {
        loading.value = false;
    }
}

// 滚动到底部
function scrollBottom() {
    if (chatHistoryRef.value) {
        chatHistoryRef.value.scrollTop = chatHistoryRef.value.scrollHeight;
    }
}

// 打开历史会话弹窗
function openHistoryDialog() {
    historyDialogVisible.value = true;
}

// 打开附件列表弹窗(基于当前会话 ID)
function openAttachmentDialog() {
    if (!currentSessionId.value) {
        ElMessage.warning('当前无会话,请先发送一条消息创建会话');
        return;
    }
    attachmentDialogVisible.value = true;
}

// 选择历史会话
async function handleSessionSelect(session) {
    // 切换会话时清掉旧轮询
    stopReportPoll();
    reportGenerating.value = false;
    if (!session) {
        // 当前会话被删除后，选择为空，需要重新初始化
        currentSessionId.value = '';
        messageList.value = [];
        await initSession();
        return;
    }
    currentSessionId.value = session.sessionId;
    messageList.value = [];
    await queryChatList();
}

// 删除会话后处理
function handleSessionDelete(sessionId) {
    if (sessionId === currentSessionId.value) {
        // 删除了当前会话，需要重新初始化
        currentSessionId.value = '';
        messageList.value = [];
        initSession();
    }
}

// 新建会话
async function createNewSessionFun() {
    try {
        const res = await createNewSessionApi(userStore.userId);
        if (res?.code === 200 && res.data) {
            currentSessionId.value = res.data.sessionId;
            messageList.value = [];
        }
    } catch (error) {
        console.error('创建会话失败:', error);
    }
}
</script>

<style>
.drawer-body {
    padding: 0 !important;
}
</style>

<style lang="scss" scoped>
.drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid #eee;
    background: #fff;
}

.drawer-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
}

.header-actions {
    display: flex;
    gap: 4px;

    :deep(.el-button) {
        font-size: 13px;
    }
}

.ai-chat {
    display: flex;
    flex-direction: column;
    height: calc(100% - 51px);
    border: 1px solid #eee;
    border-radius: 4px;
    overflow: hidden;
}

.chat-history {
    flex: 1;
    overflow-y: auto;
    padding: 15px;
    background: #fafafa;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #909399;

    p {
        margin-top: 12px;
        font-size: 14px;
    }
}

// AI思考中状态
.thinking-indicator {
    margin-bottom: 10px;
    animation: fadeIn 0.3s ease;

    .ai-side {
        display: flex;
        align-items: flex-start;
        max-width: 75%;

        .content {
            background: white;
            padding: 8px 12px;
            border-radius: 12px 12px 12px 4px;
            margin-right: 10px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
    }

    .empty-side {
        visibility: hidden;
        min-width: 40px;
    }
}

.thinking-dots {
    display: flex;
    gap: 4px;

    .dot {
        width: 8px;
        height: 8px;
        background: #909399;
        border-radius: 50%;
        animation: bounce 1.4s infinite ease-in-out both;

        &:nth-child(1) {
            animation-delay: -0.32s;
        }

        &:nth-child(2) {
            animation-delay: -0.16s;
        }
    }
}

@keyframes bounce {

    0%,
    80%,
    100% {
        transform: scale(0);
    }

    40% {
        transform: scale(1);
    }
}

// 报告生成中提示条
.report-generating-indicator {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 6px 0;
    padding: 8px 14px;
    background: #f6ffed;
    border: 1px solid #b7eb8f;
    border-radius: 16px;
    font-size: 12px;
    color: #389e0d;
    animation: pulse 1.5s ease-in-out infinite;

    .report-icon {
        animation: spin 2s linear infinite;
    }
}

@keyframes pulse {

    0%,
    100% {
        opacity: 1;
    }

    50% {
        opacity: 0.6;
    }
}

@keyframes spin {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}

.chat-input {
    display: flex;
    padding: 12px;
    background: white;
    border-top: 1px solid #eee;
    gap: 10px;

    textarea {
        flex: 1;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        resize: none;
        font-size: 14px;
        line-height: 1.5;

        &:focus {
            outline: none;
            border-color: #1890ff;
        }

        &:disabled {
            background: #f5f5f5;
            cursor: not-allowed;
        }
    }

    button {
        padding: 8px 20px;
        background: linear-gradient(135deg, #1890ff, #40a9ff);
        color: white;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        font-size: 14px;

        &:hover:not(:disabled) {
            box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
        }

        &:disabled {
            background: #d9d9d9;
            cursor: not-allowed;
        }
    }
}
</style>
